import { VisibilityOff, Visibility, Close, Edit } from "@mui/icons-material"
import { Grid, TextField, InputAdornment, IconButton, Tooltip } from "@mui/material"
import { useState } from "react"
import { Control, Controller, FieldError, UseFormGetValues, UseFormReset } from "react-hook-form"

interface ProfileFormValues {
  firstName: string;
  lastName: string;
  email: string;
  primaryPhone: string;
  bio: string;
  newPassword: string;
  confirmPassword: string;
};

interface PasswordSectionProps {
  // eslint-disable-next-line
  control: Control<ProfileFormValues, any, ProfileFormValues> //react-hook-form has set it's type to any, so we bound
  resetProfile: UseFormReset<ProfileFormValues>
  newPasswordError: FieldError | undefined
  confirmNewPassword: FieldError | undefined
  getValues: UseFormGetValues<ProfileFormValues>
}
const PasswordSection: React.FC<PasswordSectionProps> = ({ control, resetProfile, newPasswordError, confirmNewPassword, getValues }) => {
  const [passwordEditable, setPasswordEditable] = useState(false)
  const [passwordVisible, setPasswordVisible] = useState(false)

  const handlePasswordCancel = () => {
    setPasswordEditable(prev => !prev)
    resetProfile({
      newPassword: "",
      confirmPassword: "",
    });
  }

  return (
    <Grid container justifyContent='space-between' alignItems='center'>
      <Grid size={5.5}>
        <Controller
          name="newPassword"
          control={control}
          rules={{
            validate: passwordEditable ? {
              minLength: (v) =>
                v.length >= 8 || "Password must be at least 8 characters.",
              hasUppercase: (v) =>
                /[A-Z]/.test(v) || "Password must contain an uppercase letter.",
              hasNumber: (v) =>
                /[0-9]/.test(v) || "Password must contain a number.",
              hasSpecialChar: (v) =>
                /[!@#$%^&*(),.?":{}|<>]/.test(v) ||
                "Password must contain a special character.",
            } : {},
          }}
          render={({ field }) => (
            <TextField
              fullWidth
              required
              {...field}
              label="New Password"
              margin="normal"
              variant="outlined"
              type={passwordVisible ? "text" : "password"}
              disabled={!passwordEditable}
              error={!!newPasswordError}
              helperText={newPasswordError?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setPasswordVisible((prev) => !prev)}
                        edge="end"
                        disabled={!passwordEditable}
                      >
                        {passwordVisible ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />
          )}
        />
      </Grid>
      <Grid size={5.5}>
        <Controller
          name="confirmPassword"
          control={control}
          rules={{
            validate: passwordEditable ? {
              matchesNew: (v) =>
                v === getValues("newPassword") || "Passwords do not match.",
              minLength: (v) =>
                v.length >= 8 || "Password must be at least 8 characters.",
              hasUppercase: (v) =>
                /[A-Z]/.test(v) || "Password must contain an uppercase letter.",
              hasNumber: (v) =>
                /[0-9]/.test(v) || "Password must contain a number.",
              hasSpecialChar: (v) =>
                /[!@#$%^&*(),.?":{}|<>]/.test(v) ||
                "Password must contain a special character.",
            } : {},
          }}
          render={({ field }) => (
            <TextField
              fullWidth
              required
              {...field}
              label="Confirm Password"
              margin="normal"
              variant="outlined"
              type={passwordVisible ? "text" : "password"}
              disabled={!passwordEditable}
              error={!!confirmNewPassword}
              helperText={confirmNewPassword?.message}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setPasswordVisible((prev) => !prev)}
                        edge="end"
                        disabled={!passwordEditable}
                      >
                        {passwordVisible ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />
          )}
        />
      </Grid>
      <Tooltip title={passwordEditable ? "Cancel" : "Change Password"}>
        <IconButton sx={{ width: 30, height: 30 }} onClick={handlePasswordCancel}>
          {passwordEditable ? <Close /> : <Edit />}
        </IconButton>
      </Tooltip>
    </Grid>
  );
}

export default PasswordSection
